import { useEffect, useState } from "react";
import {
  Plus,
  Trash2,
  AlertTriangle,
  History,
  MessageSquare
} from "lucide-react";
import api from "../../../../api";
import { useModal } from "../../../../context/ModalContext";
import Tooltip from "../../../../components/ui/Tooltip";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/Input";
import CustomSelect from "../../../../components/ui/CustomSelect";

export default function FamilleInteractions({ famille }) {
  const [showForm, setShowForm] = useState(false);
  const [interactions, setInteractions] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [formData, setFormData] = useState({
    typeInteraction: "",
    canal: "",
    date: "",
    contenu: "",
    prochaineEtape: "",
    dateProchaineInteraction: "",
    important: false,
    commentaireInterne: "",
  });

  const { confirm } = useModal();
  const familleId = famille?.id || famille?._id;

  useEffect(() => {
    if (familleId) fetchInteractions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familleId]);

  const fetchInteractions = async () => {
    if (!familleId) return;
    try {
      const res = await api.get(`/interactions/${familleId}`);
      const sorted = res.data.sort((a, b) => new Date(b.date) - new Date(a.date));
      setInteractions(sorted);
    } catch (err) {
      console.error("Erreur récupération interactions :", err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleAddInteraction = async () => {
    const { typeInteraction, canal, date, contenu } = formData;
    if (!typeInteraction || !canal || !date || !contenu) {
      setErrorMessage("Tous les champs obligatoires doivent être remplis.");
      return;
    }

    try {
      await api.post("/interactions", {
        ...formData,
        prospectId: familleId,
        referent: famille.referent,
        statut: "Client",
      });

      fetchInteractions();
      setShowForm(false);
      setFormData({
        typeInteraction: "",
        canal: "",
        date: "",
        contenu: "",
        prochaineEtape: "",
        dateProchaineInteraction: "",
        important: false,
        commentaireInterne: "",
      });
      setErrorMessage("");
    } catch (err) {
      console.error("Erreur ajout interaction :", err);
      setErrorMessage("Erreur lors de l'ajout de l'interaction.");
    }
  };

  const handleDelete = async (id) => {
    const isConfirmed = await confirm({
      title: "Supprimer l'interaction",
      message: "Voulez-vous vraiment supprimer cette interaction ?",
      type: "danger",
      confirmText: "Supprimer",
      cancelText: "Annuler",
    });
    if (!isConfirmed) return;
    try {
      await api.delete(`/interactions/${id}`);
      fetchInteractions();
    } catch (err) {
      console.error("Erreur suppression :", err);
    }
  };

  if (!famille) {
    return (
      <div className="flex flex-col items-center justify-center text-center bg-surface shadow-md rounded-lg h-[300px] w-full p-6 space-y-4">
        <img src={'/assets/logo-hipparchus.png'} alt="Logo Nanthiat" className="w-20 h-20" />
        <p className="text-subtle">
          Sélectionnez une famille pour voir son historique d’interactions.
        </p>
      </div>
    );
  }

  return (
    <div className="workspace-card p-5 rounded-[var(--hp-radius-lg)] border border-workspace bg-surface shadow-hp-sm w-full flex flex-col justify-between">
      <div className="flex-1 mb-2">
        <div className="flex items-center justify-between pb-3 border-b border-workspace mb-3">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-secondary" />
            <h2 className="text-base font-bold text-primary">Dernières interactions</h2>
          </div>
          <Button
            variant="accent"
            size="sm"
            onClick={() => setShowForm(true)}
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            Ajouter
          </Button>
        </div>

        {interactions.length > 0 ? (
          <div className="space-y-2.5 pr-1 max-h-[260px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
            {interactions.map((item, index) => (
              <div
                key={item.id || item._id || index}
                className="relative border-l-4 border-secondary pl-3 pr-8 py-2.5 bg-surface-muted/30 rounded-r-lg border border-workspace text-xs"
              >
                {/* Icône important animée */}
                {item.important && (
                  <div className="absolute top-2 right-2 text-warning-500 animate-pulse">
                    <Tooltip content="Important">
                      <AlertTriangle className="w-4 h-4" />
                    </Tooltip>
                  </div>
                )}

                <p className="text-xs text-primary font-bold mb-0.5">
                  {item.typeInteraction} via {item.canal} –{" "}
                  {new Date(item.date).toLocaleDateString("fr-FR")}
                </p>

                <p className="text-xs text-muted">{item.contenu}</p>

                {item.prochaineEtape && (
                  <p className="text-xs text-secondary mt-1 font-medium">
                    📌 {item.prochaineEtape}{" "}
                    {item.dateProchaineInteraction &&
                      `(le ${new Date(item.dateProchaineInteraction).toLocaleDateString("fr-FR")})`}
                  </p>
                )}

                {/* Corbeille en bas à droite */}
                <div className="absolute bottom-2 right-2">
                  <Tooltip content="Supprimer">
                    <button
                      onClick={() => handleDelete(item.id || item._id)}
                      className="text-danger-600 hover:text-danger-800 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </Tooltip>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-center text-xs text-subtle italic flex flex-col items-center gap-2">
            <MessageSquare className="w-8 h-8 text-subtle opacity-50" />
            <span>Aucune interaction consignée pour ce dossier.</span>
          </div>
        )}
      </div>

      {/* Modal ajout d’interaction */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-surface w-full max-w-2xl rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Nouvelle interaction</h3>
            {errorMessage && (
              <div className="mb-4 bg-danger-100 border border-danger-400 text-danger-700 px-4 py-2 rounded">
                {errorMessage}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="col-span-1">
                <label className="block text-sm font-medium">Type d'interaction *</label>
                <CustomSelect
                  value={formData.typeInteraction}
                  onChange={(val) => setFormData({ ...formData, typeInteraction: val })}
                  options={["Appel", "Email", "Rendez-vous", "Message LinkedIn", "Note interne", "Autre"].map(opt => ({ label: opt, value: opt }))}
                  placeholder="-- Sélectionner --"
                />
              </div>

              <div className="col-span-1">
                <label className="block text-sm font-medium">Canal *</label>
                <CustomSelect
                  value={formData.canal}
                  onChange={(val) => setFormData({ ...formData, canal: val })}
                  options={["Téléphone", "Email", "LinkedIn", "Visio", "WhatsApp", "Autre"].map(opt => ({ label: opt, value: opt }))}
                  placeholder="-- Sélectionner --"
                />
              </div>

              <div className="col-span-1">
                <label className="block text-sm font-medium">Date *</label>
                <Input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                />
              </div>

              <div className="col-span-1">
                <label className="block text-sm font-medium">Prochaine étape</label>
                <Input
                  name="prochaineEtape"
                  value={formData.prochaineEtape}
                  onChange={handleChange}
                />
              </div>

              <div className="col-span-1">
                <label className="block text-sm font-medium">Date prochaine interaction</label>
                <Input
                  type="date"
                  name="dateProchaineInteraction"
                  value={formData.dateProchaineInteraction}
                  onChange={handleChange}
                />
              </div>

              <div className="col-span-1 flex items-center gap-2">
                <input
                  type="checkbox"
                  name="important"
                  checked={formData.important}
                  onChange={handleChange}
                  className="h-4 w-4 text-secondary"
                />
                <label className="text-sm">Marquer comme important</label>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium">Contenu *</label>
                <textarea
                  name="contenu"
                  value={formData.contenu}
                  onChange={handleChange}
                  rows={3}
                  className="workspace-input w-full"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium">Commentaire interne</label>
                <textarea
                  name="commentaireInterne"
                  value={formData.commentaireInterne || ""}
                  onChange={handleChange}
                  rows={2}
                  className="workspace-input w-full"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-6">
              <Button
                variant="outline"
                onClick={() => setShowForm(false)}
              >
                Annuler
              </Button>
              <Button
                variant="primary"
                onClick={handleAddInteraction}
              >
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}